import { format } from 'date-fns';
import type { InteractionMessageResponse } from '@vagusx/clients';
import { downloadBlob } from '@/lib/download';
import { callEndReasonLabel } from './call-end-reason';

export function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

/**
 * How long a wait is, for the silence marker between two lines — `16.4s`, or
 * `2:05` once it is long enough that seconds stop being readable.
 *
 * One decimal below a minute because the interesting range is single-digit to
 * low-double-digit seconds, where truncating to `16s` throws away the
 * difference between a pause and a problem. `formatDuration` is the right
 * shape for a position in a file and the wrong one for a measured interval.
 */
export function formatGapDuration(ms: number): string {
  // Round ONCE, then both branch and format from the rounded value. Branching
  // on raw ms sent `59_999` down the seconds path, which printed `60.0s` — a
  // value the branch it took says is impossible. Rounding only inside that
  // branch instead trades it for `0:59`, since `formatDuration` truncates. One
  // rounded number feeding both decisions is the only version where the label
  // and the branch agree.
  const rounded = Math.round(ms / 100) * 100;
  if (rounded < 60_000) return `${(rounded / 1000).toFixed(1)}s`;
  return formatDuration(rounded);
}

/**
 * The label on the transport's rate button — `1×`, `1.25×`, `2×`.
 *
 * Trailing zeros are dropped so a whole speed reads as `2×` rather than
 * `2.00×`: the button is a fixed-width control in a crowded bar, and the
 * common speeds are the ones that should stay shortest.
 */
export function formatPlaybackRate(rate: number): string {
  return `${Number(rate.toFixed(2))}×`;
}

/**
 * Render a transcript as plain text. Keeps user/agent speech lines (tool events
 * omitted) plus a trailing `call_ended` marker. Pure — exported for unit tests.
 */
export function buildTranscriptText(messages: InteractionMessageResponse[]): string {
  // Mirror the on-screen ordering: speech lines in order, then any call_ended
  // marker last (its sequence can race a late transcript write — see
  // pairToolCalls / ADR-116).
  const lines: string[] = [];
  const terminal: string[] = [];
  for (const m of messages) {
    const time = format(new Date(m.timestamp), 'HH:mm:ss');
    if (m.kind === 'call_ended') {
      terminal.push(`[${time}] — Call ended: ${callEndReasonLabel(m.payload.reason)} —`);
    } else if (m.kind === 'user_message' || m.kind === 'agent_message') {
      const speaker = m.kind === 'user_message' ? 'Caller' : 'Agent';
      lines.push(`[${time}] ${speaker}: ${m.text}`);
    }
  }
  return [...lines, ...terminal].join('\n');
}

/**
 * Default transcript-as-text downloader suitable for wiring to
 * `header.onDownload`. Consumers can pass their own if they need
 * CSV/JSON.
 */
export function downloadTranscriptAsText(
  messages: InteractionMessageResponse[],
  filename = 'transcript.txt'
): void {
  const blob = new Blob([buildTranscriptText(messages)], {
    type: 'text/plain;charset=utf-8',
  });
  downloadBlob(blob, filename);
}
